import {Component, inject, OnInit, signal} from '@angular/core'
import {FormControl, ReactiveFormsModule} from '@angular/forms'
import {MatIconButton, MatMiniFabButton} from '@angular/material/button'
import {MatCard, MatCardContent} from '@angular/material/card'
import {MatFormField, MatLabel} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {MatPaginator} from '@angular/material/paginator'
import {MatOption, MatSelect} from '@angular/material/select'
import {MatSort, MatSortHeader} from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table'
import {MatTooltip} from '@angular/material/tooltip'
import {BankIDVerificationData} from '@sparbanken-syd/id-be'
import {SparbankenUser} from '@sparbanken-syd/sparbanken-syd-bankid'
import {forkJoin, of, startWith, switchMap} from 'rxjs'
import {DataService} from '../services/data.service'
import {UserService} from '../services/user.service'
import {BaseTable, ConfirmComponent, IConfirmComponentData} from '@sparbanken-syd/sparbanken-syd-theme'
import {MatProgressBar} from '@angular/material/progress-bar'
import {MatSnackBar} from '@angular/material/snack-bar'
import {Clipboard} from '@angular/cdk/clipboard'
import {MatDialog} from '@angular/material/dialog'
import {DatePipe} from '@angular/common'
import {DOCUMENT} from '../application/app'

@Component({
  selector: 'spb-id-list',
  templateUrl: './id-list.component.html',
  styleUrls: ['./id-list.component.scss'],

  imports: [
    MatFormField,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatIcon,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatTooltip,
    MatCellDef,
    MatCell,
    MatPaginator,
    MatNoDataRow,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    MatLabel,
    MatInput,
    MatMiniFabButton,
    MatCard,
    MatCardContent,
    MatIconButton,
    MatProgressBar,
    DatePipe
  ]
})
export class IdListComponent extends BaseTable<BankIDVerificationData> implements OnInit {

  public filterControl = new FormControl<string>('', {nonNullable: true})
  public copyText = ''
  public offices = signal<string[]>([])
  public showTable = signal<boolean>(false)

  /**
   * Used when updating list
   */
  public users: SparbankenUser[] = []


  public override columns = signal(['timeStamp', 'personalNumber', 'name', 'age', 'coworkerName', 'office', 'copy', 'save'])

  private readonly dataService = inject(DataService)
  private readonly userService = inject(UserService)

  private readonly snack = inject(MatSnackBar)
  private readonly clip = inject(Clipboard)
  private readonly dialog = inject(MatDialog)

  private readonly document = inject(DOCUMENT)

  public ngOnInit() {
    this.filterControl.valueChanges.pipe(
      startWith('')
    ).subscribe((value: string) =>
      this.items.filter = value.trim().toLowerCase()
    )
    this.loadList()
    /**
     * Get all sparbanken offices
     */
    this.getOffices()
  }

  public copyInfo(bankIDVerificationData: BankIDVerificationData) {
    const jsonData = JSON.stringify(bankIDVerificationData)
    this.clip.copy(jsonData)
    this.snack.open('Kopierad till urklipp', 'OK', {
      horizontalPosition: 'right',
      verticalPosition: 'top',
      duration: 3000
    })

  }

  public saveInfo(bankIDVerificationData: BankIDVerificationData) {
    if (!bankIDVerificationData.user) {
      this.dialog.open<ConfirmComponent, IConfirmComponentData, boolean>(ConfirmComponent, {
        data: {
          title: 'Fel',
          text: 'Felaktig verifiering - Kan inte sparas'
        }
      })
      return
    }
    // Remove undefined and gdprRemove properties from the object
    for (const prop in bankIDVerificationData) {
      if (bankIDVerificationData[prop as keyof BankIDVerificationData] === undefined || prop === 'gdprRemove') {
        delete bankIDVerificationData[prop as keyof BankIDVerificationData]
      }
    }

    // Convert data to JSON format
    const json_data = JSON.stringify(bankIDVerificationData)

    /// Create the filename based on the scanned identification number and name
    const sub = bankIDVerificationData.user.personalNumber
    const name = bankIDVerificationData.user.name
    const filename = `${sub}_${name}_intyg.txt`

    // Create blog and url to click
    const blob = new Blob([json_data], {type: 'application/json'})
    const url = URL.createObjectURL(blob)
    const downloadLink = this.document.createElement('a')
    downloadLink.href = url
    downloadLink.download = filename
    downloadLink.click()

    // Clear the URL object after the download
    URL.revokeObjectURL(url)
  }

  public loadList() {
    this.showTable.set(false)
    this.dataService.getAllTransactions()
      .pipe(
        switchMap(bid => forkJoin([of(bid), this.userService.getUsers()]))
      )
      .subscribe(([bid, users]) => {
        this.users = users
        this.items.data = bid
          .toSorted((a, b) => b.timeStamp - a.timeStamp)
          .map(item => this.transformTransactionData(item))
        this.showTable.set(true)
      })
  }


  /**
   * Make gdprRemove date more readable
   */
  public convertDate(timestamp: number) {
    const date = new Date(timestamp * 1000)
    const convertedDate = date.toLocaleDateString('sv-SE')
    return timestamp ? `Gallras ${convertedDate}` : ''
  }

  private getOffices(): void {
    this.userService.getOffices()
      .subscribe(offices => this.offices.set(offices))
  }

  private transformTransactionData(item: BankIDVerificationData) {
    const user = this.users.find((user: SparbankenUser) => user.sub === item.sub)!
    return {
      ...item,
      name: item.user?.name,
      age: item.user?.age,
      timeStamp: item.timeStamp,
      personalNumber: item.user?.personalNumber,
      coworkerName: user?.name ?? item.sub,
      office: user?.office ?? ''
    } as any
  }
}